if (!customElements.get('back-in-stock')) {
  const KLAVIYO_BACK_IN_STOCK_ENDPOINT = 'https://a.klaviyo.com/client/back-in-stock-subscriptions/';

  const escapeHtml = (value) =>
    String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');

  function buildKlaviyoPayload(email, variantId) {
    return {
      data: {
        type: 'back-in-stock-subscription',
        attributes: {
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email,
              },
            },
          },
          channels: ['EMAIL'],
        },
        relationships: {
          variant: {
            data: {
              type: 'catalog-variant',
              id: `$shopify:::$default:::${variantId}`,
            },
          },
        },
      },
    };
  }

  function buildKlaviyoUrl(companyId) {
    const params = new URLSearchParams({ company_id: companyId });
    return `${KLAVIYO_BACK_IN_STOCK_ENDPOINT}?${params}`;
  }

  function normalizeVariantTitle(value) {
    const title = String(value || '').trim();
    return title.toLowerCase() === 'default title' ? '' : title;
  }

  customElements.define(
    'back-in-stock',
    class BackInStock extends HTMLElement {
      constructor() {
        super();
        this.isConnectedToDom = false;
        this.renderQueued = false;
        this.overlay = null;
        this.openPopup = this.openPopup.bind(this);
        this.closePopup = this.closePopup.bind(this);
        this.handleSubmit = this.handleSubmit.bind(this);
        this.handleOverlayClick = this.handleOverlayClick.bind(this);
        this.handleKeydown = this.handleKeydown.bind(this);
      }

      static get observedAttributes() {
        return ['product_title', 'variant_title', 'variant_id', 'company_id'];
      }

      connectedCallback() {
        this.isConnectedToDom = true;
        this.queueRender();
      }

      disconnectedCallback() {
        this.isConnectedToDom = false;
        document.removeEventListener('keydown', this.handleKeydown);
        document.body.classList.remove('back-in-stock-modal-open');
        this.overlay?.remove();
        this.overlay = null;
      }

      attributeChangedCallback(_name, oldValue, newValue) {
        if (oldValue !== newValue) this.queueRender();
      }

      get productTitle() {
        return this.getAttribute('product_title') || 'Product';
      }

      get variantTitle() {
        return normalizeVariantTitle(this.getAttribute('variant_title'));
      }

      get variantId() {
        return this.getAttribute('variant_id') || '';
      }

      get companyId() {
        return this.getAttribute('company_id') || 'WNF3cp';
      }

      buildOverlay() {
        const productTitle = escapeHtml(this.productTitle);
        const variantTitle = escapeHtml(this.variantTitle);
        const variantId = escapeHtml(this.variantId);

        const overlay = document.createElement('div');
        overlay.className = 'back-in-stock-popup-overlay';
        overlay.innerHTML = `
          <div class="back-in-stock-popup" role="dialog" aria-modal="true" aria-labelledby="back-in-stock-heading">
            <button class="close-btn" type="button" aria-label="Close">&times;</button>
            <h4 class="back-in-stock-heading" id="back-in-stock-heading">${productTitle}</h4>
            ${variantTitle ? `<h5 class="back-in-stock-variant-heading">${variantTitle}</h5>` : ''}
            <p class="back-in-stock-text">Enter your email and we'll let you know when this is back.</p>
            <form class="back-in-stock-form">
              <input type="email" name="email" placeholder="Enter your email" required>
              <input type="hidden" name="variant_id" value="${variantId}">
              <button class="back-in-stock-submit" type="submit">Notify me when available</button>
              <p class="back-in-stock-error-msg" role="alert" style="display:none;"></p>
              <p class="back-in-stock-success-msg" role="status" style="display:none;">You're in! We'll let you know when it's back.</p>
            </form>
          </div>
        `;
        document.body.append(overlay);
        return overlay;
      }

      renderComponent() {
        this.overlay?.remove();
        this.overlay = this.buildOverlay();

        this.innerHTML = '<button class="trigger-back-in-stock-btn" type="button">Notify me when available</button>';

        this.closeButton = this.overlay.querySelector('.close-btn');
        this.openButton = this.querySelector('.trigger-back-in-stock-btn');
        this.form = this.overlay.querySelector('.back-in-stock-form');
        this.emailInput = this.form?.querySelector('input[name="email"]');
        this.variantInput = this.form?.querySelector('input[name="variant_id"]');
        this.errorMessage = this.overlay.querySelector('.back-in-stock-error-msg');
        this.successMessage = this.overlay.querySelector('.back-in-stock-success-msg');

        this.attachEventListeners();
      }

      queueRender() {
        if (!this.isConnectedToDom || this.renderQueued) return;

        this.renderQueued = true;
        queueMicrotask(() => {
          this.renderQueued = false;
          if (this.isConnected) this.renderComponent();
        });
      }

      attachEventListeners() {
        this.openButton?.addEventListener('click', this.openPopup);
        this.closeButton?.addEventListener('click', this.closePopup);
        this.overlay?.addEventListener('click', this.handleOverlayClick);
        this.form?.addEventListener('submit', this.handleSubmit);
      }

      openPopup() {
        this.overlay?.classList.add('active');
        document.body.classList.add('back-in-stock-modal-open');
        document.addEventListener('keydown', this.handleKeydown);
        this.emailInput?.focus();
      }

      closePopup() {
        this.overlay?.classList.remove('active');
        document.removeEventListener('keydown', this.handleKeydown);
        document.body.classList.remove('back-in-stock-modal-open');
        this.openButton?.focus();
      }

      handleOverlayClick(event) {
        if (event.target === this.overlay) this.closePopup();
      }

      handleKeydown(event) {
        if (event.key === 'Escape') this.closePopup();
      }

      handleSubmit(event) {
        event.preventDefault();

        const email = this.emailInput?.value.trim() || '';
        const variantId = this.variantInput?.value.trim() || '';
        const companyId = this.companyId.trim();

        if (!email || !variantId) {
          this.emailInput?.reportValidity();
          return;
        }

        if (!companyId) {
          this.showError();
          return;
        }

        if (this.errorMessage) {
          this.errorMessage.style.display = 'none';
          this.errorMessage.textContent = '';
        }

        fetch(buildKlaviyoUrl(companyId), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            revision: '2024-06-15',
          },
          body: JSON.stringify(buildKlaviyoPayload(email, variantId)),
        })
          .then((result) => {
            if (result.status !== 202) throw new Error(`Klaviyo request failed (${result.status})`);

            this.form?.reset();
            if (!this.successMessage) return;

            this.successMessage.style.display = 'block';

            setTimeout(() => {
              this.successMessage.style.display = 'none';
              this.closePopup();
            }, 2000);
          })
          .catch(() => {
            this.showError();
          });
      }

      showError() {
        if (!this.errorMessage) return;
        this.errorMessage.textContent = 'Something went wrong. Please try again later.';
        this.errorMessage.style.display = 'block';
      }
    },
  );
}
